import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Loader2, CheckCircle } from "lucide-react";

interface OnboardingFlowProps {
  open: boolean;
  onComplete: () => void;
}

export function OnboardingFlow({ open, onComplete }: OnboardingFlowProps) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [profileData, setProfileData] = useState({
    name: "",
    title: "",
    institution: "",
    department: "",
  });

  const [roleData, setRoleData] = useState({
    role: "",
  });

  const [tierData, setTierData] = useState({
    market_segment: "",
  });

  const totalSteps = 4;
  const progress = (step / totalSteps) * 100;

  const handleNext = async () => {
    if (step === 1) {
      if (!profileData.name || !profileData.institution) {
        toast.error("Please fill in all required fields");
        return;
      }
      setStep(2);
    } else if (step === 2) {
      if (!roleData.role) {
        toast.error("Please select your role");
        return;
      }
      setStep(3);
    } else if (step === 3) {
      if (!tierData.market_segment) {
        toast.error("Please select your institution tier");
        return;
      }
      setStep(4);
      await saveOnboarding();
    }
  };

  const saveOnboarding = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");

      // Update profile
      const { error: profileError } = await supabase
        .from("profiles" as any)
        .update({
          name: profileData.name,
          title: profileData.title,
          institution: profileData.institution,
          department: profileData.department,
        } as any)
        .eq("id", user.id);

      if (profileError) throw profileError;

      // Assign role via edge function (secure server-side validation)
      const { data: roleResponse, error: roleError } = await supabase.functions.invoke(
        'assign-onboarding-role',
        {
          body: { role: roleData.role }
        }
      );

      if (roleError) throw new Error(roleError.message);
      if (roleResponse?.error) throw new Error(roleResponse.error);
      
      const isRoleUpdated = roleResponse?.updated;

      // Provision institution settings based on selected tier
      const { data: settingsResponse, error: settingsError } = await supabase.functions.invoke(
        'provision-institution-settings',
        {
          body: { 
            market_segment: tierData.market_segment,
            institution_name: profileData.institution 
          }
        }
      );

      if (settingsError) throw new Error(settingsError.message);
      if (settingsResponse?.error) throw new Error(settingsResponse.error);

      // Show success message based on whether it was an update or new setup
      const message = isRoleUpdated 
        ? "Profile updated successfully!" 
        : "Profile setup complete!";
      
      toast.success(message);
      setTimeout(() => {
        onComplete();
      }, 2000);
    } catch (error: any) {
      console.error("Onboarding error:", error);
      toast.error("Error saving profile: " + error.message);
      setStep(3);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="max-w-2xl" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Welcome to AthleteNexus!</DialogTitle>
          <DialogDescription>
            Let's get your account set up in a few quick steps
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <Progress value={progress} className="w-full" />

          {step === 1 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Step 1: Your Information</h3>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    value={profileData.name}
                    onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                    placeholder="John Doe"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="institution">Institution *</Label>
                  <Input
                    id="institution"
                    value={profileData.institution}
                    onChange={(e) => setProfileData({ ...profileData, institution: e.target.value })}
                    placeholder="University Name"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Title</Label>
                    <Input
                      id="title"
                      value={profileData.title}
                      onChange={(e) => setProfileData({ ...profileData, title: e.target.value })}
                      placeholder="Compliance Officer"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="department">Department</Label>
                    <Input
                      id="department"
                      value={profileData.department}
                      onChange={(e) => setProfileData({ ...profileData, department: e.target.value })}
                      placeholder="Athletics"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Step 2: Select Your Role</h3>
              <div className="space-y-2">
                <Label htmlFor="role">Role *</Label>
                <Select
                  value={roleData.role}
                  onValueChange={(value) => setRoleData({ role: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select your role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="athletic_director">Athletic Director</SelectItem>
                    <SelectItem value="compliance_officer">Compliance Officer</SelectItem>
                    <SelectItem value="coach">Coach</SelectItem>
                    <SelectItem value="staff">Staff</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">
                  This determines your access level and permissions in the system
                </p>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Step 3: Choose Your Tier</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Select the tier that best matches your institution's needs and budget
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setTierData({ market_segment: 'power5' })}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    tierData.market_segment === 'power5'
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="font-semibold text-lg mb-2">Power 5 / FBS</div>
                  <div className="text-sm text-muted-foreground mb-3">Enterprise Tier</div>
                  <div className="text-xs space-y-1">
                    <div>✓ All 9 features included</div>
                    <div>✓ Deal pipeline & contract versioning</div>
                    <div>✓ Agent & brand marketplaces</div>
                    <div>✓ Monetization hub & ROI tracking</div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setTierData({ market_segment: 'fcs' })}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    tierData.market_segment === 'fcs'
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="font-semibold text-lg mb-2">FCS / Group of 5</div>
                  <div className="text-sm text-muted-foreground mb-3">Growth Tier</div>
                  <div className="text-xs space-y-1">
                    <div>✓ 6 features included</div>
                    <div>✓ Deal pipeline & brand marketplace</div>
                    <div>✓ Conference partnership</div>
                    <div>✓ ROI tracking & real-time alerts</div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setTierData({ market_segment: 'div2' })}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    tierData.market_segment === 'div2'
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="font-semibold text-lg mb-2">Div II / Div III</div>
                  <div className="text-sm text-muted-foreground mb-3">Starter Tier</div>
                  <div className="text-xs space-y-1">
                    <div>✓ 3 core features</div>
                    <div>✓ ROI tracking</div>
                    <div>✓ Real-time alerts</div>
                    <div>✓ Compliance audit tool</div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setTierData({ market_segment: 'naia' })}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    tierData.market_segment === 'naia'
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="font-semibold text-lg mb-2">NAIA / High School</div>
                  <div className="text-sm text-muted-foreground mb-3">Essentials Tier</div>
                  <div className="text-xs space-y-1">
                    <div>✓ 2 essential features</div>
                    <div>✓ Real-time alerts</div>
                    <div>✓ Compliance audit tool</div>
                    <div>✓ Basic compliance management</div>
                  </div>
                </button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4 text-center py-8">
              {loading ? (
                <>
                  <Loader2 className="h-16 w-16 animate-spin mx-auto text-primary" />
                  <h3 className="text-lg font-semibold">Setting up your account...</h3>
                </>
              ) : (
                <>
                  <CheckCircle className="h-16 w-16 mx-auto text-green-600" />
                  <h3 className="text-lg font-semibold">All set!</h3>
                  <p className="text-muted-foreground">
                    Redirecting you to your dashboard...
                  </p>
                </>
              )}
            </div>
          )}

          {step < 4 && (
            <div className="flex justify-between">
              <Button
                variant="outline"
                onClick={() => setStep(Math.max(1, step - 1))}
                disabled={step === 1}
              >
                Back
              </Button>
              <Button onClick={handleNext}>
                {step === 3 ? "Complete Setup" : "Next"}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
